/* eslint-disable no-shadow */
import { useState } from 'react';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@taxdown/zeno-sama-query';
import SatService from 'Services/sat';
import { SatResponseType } from 'Services/sat/types';
import Notify, { AlertTypes } from 'Services/notification';
import { getErrorDetail } from 'Services/errors';
import EventService from 'Services/events';
import i18n from 'Services/i18n';
import RecordsService from 'Services/records';
import PreviousYearsService from 'Services/previousYears';
import StorageService from 'Services/storage';
import { history, store } from 'Store';
import { setIsLoadingAction, setIsNotLoadingAction } from 'Store/modules/app/actions';
import { routes } from 'Constants/routes';
import { stages } from 'Constants/stages';
import { TAX_YEAR } from 'Config';
import {
  ErrorResponseType,
  IsPreviousYear,
  QueryKeys,
  RecordArchiveFlowResponse,
  ResponseType,
  RfcUserType,
} from 'Types/common';
import { RelevantStages } from './types';
import { ReintentsMethod } from './views/Access/components/DGForm';

export const useFetchRetrySatMutation = (
  setLoading: () => void
): UseMutationResult<unknown, unknown, unknown, unknown> =>
  useMutation([QueryKeys.RETRY_SAT_MUTATION], (): Promise<unknown> => SatService.retrySatUser(), {
    onSuccess: () => {
      setLoading();
    },
  });

const getUserDataSelector = (data: ResponseType<SatResponseType>): SatResponseType => {
  const userData = data.data;
  return userData.item;
};

const isPreviousYearProduct = (isPreviousYear?: IsPreviousYear): boolean | undefined => {
  if (isPreviousYear?.products?.isPreviousYear) {
    PreviousYearsService.getPreviousYearUser();
    return isPreviousYear?.products?.isPreviousYear;
  }

  return false;
};

export const validateUserCredentials = (
  data?: SatResponseType,
  isPreviousYears?: IsPreviousYear
): number | false => {
  if (!data) {
    return 500;
  }
  const eventsLogic = (): false => {
    EventService.taxAgencyCredentialUpdate(TAX_YEAR, data?.credentialType);
    store.dispatch(setIsNotLoadingAction());
    return false;
  };
  const redirectCondition = RelevantStages.includes(data.stage.value);
  const isPaProduct = isPreviousYearProduct(isPreviousYears);

  if (redirectCondition) {
    history.push(routes.PERSONAL_PHONE, {
      products: isPaProduct && {
        isPreviousYear: true,
      },
    });
    return eventsLogic();
  }

  if (data.stage.value === stages.VALIDATING_CREDENTIALS_ERROR) {
    Notify.sendNotification(i18n.t('dg:DG.Loader.rfcerror'), AlertTypes.error);
    store.dispatch(setIsNotLoadingAction());
    return false;
  }

  return 500;
};
const CURRENT_QUIZ_KEY = 'current_quiz';

export const useFetchSatUserStage = (
  isPreviousYears: IsPreviousYear
): UseQueryResult<SatResponseType> =>
  useQuery(
    [QueryKeys.FETCH_SAT_STAGE_USER],
    (): Promise<ResponseType<SatResponseType>> =>
      SatService.getSatUser({ password: true, efirma: true }),
    {
      select: data => getUserDataSelector(data),
      onSuccess: data => {
        if (
          data.stage.taxYear === TAX_YEAR &&
          (data?.stage?.value === stages.DATA_PULLING ||
            data?.stage?.value === stages.DATA_PARTIAL_PULLED ||
            data?.stage?.value === stages.DATA_RETRIEVAL)
        ) {
          const currentQuiz = StorageService.getItem(CURRENT_QUIZ_KEY);
          history.push(currentQuiz || routes.DATAGATHERING_QUIZ, {
            products: {
              isPreviousYear: isPreviousYears?.products,
            },
          });
        }
        if (data.stage.value === stages.DATA_ERROR) {
          history.push(routes.DATAGATHERING_ERROR);
        }
      },
      refetchOnWindowFocus: false,
      cacheTime: 0,
    }
  );

export enum CodeError {
  CHAPO_3003 = 'CHAPO-3003',
  YUCATAN_1016 = 'YUCATAN-1016',
  YUCATAN_1005 = 'YUCATAN-1005',
}

export const getErrorCredentials = (
  errors: ErrorResponseType[],
  setErrorType: (error: ErrorResponseType[]) => void,
  setCurrentMethod: (method: number) => void
): string => {
  const satUnavailable = errors.filter(
    error =>
      error.response.data.code === CodeError.CHAPO_3003 ||
      error.response.data.code === CodeError.YUCATAN_1016
  );
  const badCredential = errors.filter(error => error.response.data.code === CodeError.YUCATAN_1005);
  setErrorType([]);
  setCurrentMethod(1);
  if (satUnavailable.length >= 2) {
    return satUnavailable[0]?.response?.data?.detail;
  }

  return badCredential[0]?.response?.data?.detail || '';
};

export const useFetchValidatingSatCredentials = (): UseMutationResult<unknown, RfcUserType> => {
  const [errorType, setErrorType] = useState<ErrorResponseType[]>([]);
  const [currentMethod, setCurrentMethod] = useState(1);

  const mutation = useMutation([QueryKeys.SAVE_SAT_USER], SatService.saveSatUser, {
    onSuccess: () => {
      store.dispatch(setIsLoadingAction());
    },
    onError: (error: ErrorResponseType, variables: RfcUserType) => {
      setErrorType([...errorType, error as ErrorResponseType]);
      if (
        [...errorType, error].filter(error => error.response.data.code === CodeError.YUCATAN_1005)
          .length === 2
      ) {
        Notify.sendNotification(
          getErrorCredentials([...errorType, error], setErrorType, setCurrentMethod),
          AlertTypes.error
        );
        return;
      }

      if (currentMethod < 3) {
        setCurrentMethod(prev => prev + 1);
        mutation.mutate({
          ...variables,
          rfcAndPassword: {
            ...variables.rfcAndPassword,
            verificationMethod: ReintentsMethod[currentMethod + 1],
          },
        });
      } else {
        Notify.sendNotification(
          getErrorCredentials([...errorType, error], setErrorType, setCurrentMethod),
          AlertTypes.error
        );
      }
    },
    retry: false,
  });

  return {
    ...mutation,
    mutate: (credentials: RfcUserType) => {
      setCurrentMethod(1);
      mutation.mutate({
        ...credentials,
        rfcAndPassword: {
          ...credentials?.rfcAndPassword,
          verificationMethod: ReintentsMethod[1],
        },
      });
    },
  };
};

export const useFetchGetUserValidateCredentials = (
  validateCredential: boolean,
  isPreviousYears: IsPreviousYear
): UseQueryResult<SatResponseType> =>
  useQuery(
    [QueryKeys.VALIDATE_CREDENTIALS],
    async () => {
      const response = await SatService.getSatUser();
      return response;
    },
    {
      select: data => {
        const item = data.data;
        return item.item;
      },
      refetchInterval: data => validateUserCredentials(data, isPreviousYears),
      cacheTime: 0,
      enabled: validateCredential,
      refetchOnWindowFocus: false,
      onError: error => Notify.sendNotification(getErrorDetail(error), AlertTypes.error),
    }
  );

export const useFetchSatUserEfirma = (isPreviousYear?: {
  products: { isPreviousYears: boolean };
}): UseQueryResult<SatResponseType> =>
  useQuery(
    [QueryKeys.FETCH_SAT_EFIRMA_USER],
    (): Promise<ResponseType<SatResponseType>> =>
      SatService.getSatUser({ password: true, efirma: true }),
    {
      select: data => getUserDataSelector(data),
      onSuccess: data => {
        if (data.efirma && data.efirmaIsValid) {
          history.push(routes.DATAGATHERING_REFERRAL, {
            products: isPreviousYear?.products,
          });
        }
        if (data.stage.value === stages.DATA_ERROR) {
          history.push(routes.DATAGATHERING_ERROR);
        }
      },
      refetchOnWindowFocus: false,
      cacheTime: 0,
    }
  );
export const useFetchGetFeatureFlag = (): UseQueryResult<RecordArchiveFlowResponse> =>
  useQuery([QueryKeys.FETCH_GET_FEATURE_FLAG], RecordsService.getFlowFeatureFlagIsActive, {
    refetchOnWindowFocus: false,
    cacheTime: 0,
  });

export const recordCreated = (data?: RecordArchiveFlowResponse): number | false => {
  if (!data) {
    return 500;
  }

  if (data.ready) {
    history.push(routes.RESULT_XML);
  }

  return 500;
};
export const useFetchGetFeatureFlagRecord = (): UseQueryResult<RecordArchiveFlowResponse> =>
  useQuery([QueryKeys.FETCH_GET_FEATURE_FLAG], RecordsService.getFlowFeatureFlagIsActive, {
    refetchOnWindowFocus: false,
    refetchInterval: data => recordCreated(data),
    cacheTime: 0,
  });

export const useFetchRequestPreviousYears = (): UseMutationResult<
  unknown,
  unknown,
  unknown,
  unknown
> => useMutation([QueryKeys.REQUEST_PREVIOUS_YEARS], PreviousYearsService.getPreviousYearUser);

export const useRequestPreviousYears = (): UseMutationResult<unknown, unknown, unknown, unknown> =>
  useMutation(
    [QueryKeys.REQUEST_PREVIOUS_YEARS_INITIAL],
    PreviousYearsService.getPreviousYearUser,
    {}
  );
